"use client"

import * as React from "react"

import { useLocale, useTranslations } from "@/i18n"
import { useFormatPreviewDate } from "@/features/finance/components/create/finance-create-preview"
import { FundSpendingMode } from "@/features/finance/schemas/finance-configuration-forms"

/** What the "Comment cet argent peut être dépensé" sentence is written from. */
export type FundSpendingSummaryInput = {
  mode: FundSpendingMode
  /** The chosen expense accounts, by name. */
  categoryNames: readonly string[]
  /** The period, only when its switch is on (`""` otherwise). */
  periodStart: string
  periodEnd: string
  /** A dedicated fund: its activity, which overrides everything else. */
  dedicatedActivityName?: string | null
}

type SummaryTranslate = (key: string, values?: Record<string, string>) => string

/**
 * The plain-words sentence under the spending controls and in the preview:
 * "Cet argent ne pourra payer que : Hospitalité et Hébergement, du 1 novembre 2026 au 30 novembre 2026."
 * `t` is scoped to `finance.funds.spending`; `formatDate` turns a `yyyy-mm-dd` day into a long local date.
 */
export function describeFundSpending(
  input: FundSpendingSummaryInput,
  t: SummaryTranslate,
  formatDate: (value: string) => string | null,
  locale: string,
): string {
  if (input.dedicatedActivityName) return t("summaryDedicated", { activity: input.dedicatedActivityName })
  if (input.mode === FundSpendingMode.AnyExpense) return t("summaryAny")

  const from = formatDate(input.periodStart)
  const to = formatDate(input.periodEnd)
  const period =
    from && to
      ? t("periodBetween", { start: from, end: to })
      : from
        ? t("periodFrom", { date: from })
        : to
          ? t("periodUntil", { date: to })
          : null
  const names = input.categoryNames.length > 0 ? new Intl.ListFormat(locale, { type: "conjunction" }).format(input.categoryNames) : null

  if (names && period) return t("summaryOnlyDuring", { items: names, period })
  if (names) return t("summaryOnly", { items: names })
  if (period) return t("summaryAnyDuring", { period })
  return t("summaryIncomplete")
}

/** `describeFundSpending` with the reader's messages and locale. */
export function useFundSpendingSummary() {
  const t = useTranslations("finance.funds.spending")
  const locale = useLocale()
  const formatDate = useFormatPreviewDate()
  return React.useCallback(
    (input: FundSpendingSummaryInput) =>
      describeFundSpending(input, (key, values) => t(key as never, values as never), formatDate, locale),
    [formatDate, locale, t],
  )
}
